import { forwardRef, useEffect, useRef } from "react"
import { Dropdown } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { format, isValid, parseISO } from "date-fns"

// IMPORTANT: fem servir el build de browser per CRA/webpack4
import ExcelJS from "exceljs/dist/exceljs.min.js"
import { saveAs } from "file-saver"

import * as c from 'config/constants'

import Limits from "components/Limits"
import ConditionalWrapper from "components/ConditionalWrapper"
import GenericTooltip from "components/GenericTooltip"

import { useSessionContext } from 'context/SessionContext'
import { useExport } from "hooks/useExport"
import { useGroupsFiltersState, useGroupsFiltersDispatch, initialState as ist } from 'context/GroupsContext'
import { getGroups } from 'services/getGroups'

// Caràcters no permesos a Excel sheet name: [ ] \ / * ? :
const INVALID_SHEET_CHARS_RE = /[\\/*?:[\]]/g

const safeSheetName = (name) => {
  const cleaned = String(name || "Sheet")
    .replace(INVALID_SHEET_CHARS_RE, " ")
    .trim()
  return cleaned.length > 31 ? cleaned.slice(0, 31) : cleaned
}


const fmtDate = (value, fmtStr) => {
  if (!value) return "---"
  try {
    const d = parseISO(value)
    return isValid(d) ? format(d, fmtStr) : "---"
  } catch (e) {
    return "---"
  }
}

const ExportDownload = ({ data = [], dateFormat }) => {
  const { t } = useTranslation()

  // Evita descarregar múltiples cops pel mateix dataset
  const lastSigRef = useRef("")

  useEffect(() => {
    if (!Array.isArray(data) || data.length === 0) return

    const sig = `${data.length}|${data[0]?.id ?? ""}|${data[data.length - 1]?.id ?? ""}|${dateFormat ?? ""}`
    if (sig === lastSigRef.current) return
    lastSigRef.current = sig

    const run = async () => {
      const wb = new ExcelJS.Workbook()
      wb.creator = "admincv_consumer_front"
      wb.created = new Date()

      const sheetTitle = t("app.groups")
      const ws = wb.addWorksheet(safeSheetName(sheetTitle))

      // Capçaleres/columnes (mateixes que tenies a react-export-excel)
      const columns = [
        { header: t("columns.domainCode"), key: "id", width: 16 },
        { header: t("columns.groupName"), key: "group_name", width: 32 },
        { header: t("filters.semester"), key: "semester", width: 14 },
        { header: t("columns.praCampus"), key: "n_pra_campus", width: 14 },
        { header: t("columns.pdcCampus"), key: "n_pdc_campus", width: 14 },
        { header: t("columns.staffGroup"), key: "n_staff_group", width: 14 },
        { header: t("columns.studentsCampus"), key: "n_students_campus", width: 18 },
        { header: t("columns.studentsGroup"), key: "n_students_group", width: 16 },
        { header: t("columns.createDate"), key: "creation_date_fmt", width: 18 },
        { header: t("columns.syncDate"), key: "sync_date_fmt", width: 20 },
        { header: t("columns.syncStatus"), key: "sync_status_fmt", width: 14 },
        { header: t("columns.aulaType"), key: "classroom_type_key", width: 18 },
        { header: t("columns.aulaFolders"), key: "folders_create_fmt", width: 14 },
        { header: t("columns.foldersStatus"), key: "folders_status_fmt", width: 14 },
      ]

      ws.columns = columns

      const rows = data.map((col) => ({
        id: col.id,
        group_name: col.group_name,
        semester: col.semester,
        n_pra_campus: col.n_pra_campus,
        n_pdc_campus: col.n_pdc_campus,
        n_staff_group: col.n_staff_group,
        n_students_campus: col.n_students_campus,
        n_students_group: col.n_students_group,

        creation_date_fmt: fmtDate(col.creation_date, `${dateFormat}`),
        sync_date_fmt: fmtDate(col.sync_date, `${dateFormat} HH:mm`),

        sync_status_fmt:
          col.sync_status === 2
            ? t("common.ok")
            : col.sync_status === 1
              ? t("common.processing")
              : t("common.error"),

        classroom_type_key: col.classroom_type_key,

        folders_create_fmt: col.folders_create ? t("common.yes") : t("common.no"),

        folders_status_fmt:
          col.folders_create
            ? (col.folders_status ? t("common.ok") : t("common.error"))
            : "---",
      }))

      ws.addRows(rows)

      // Format bàsic
      ws.getRow(1).font = { bold: true }
      ws.views = [{ state: "frozen", ySplit: 1 }]
      ws.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: columns.length },
      }

      const buffer = await wb.xlsx.writeBuffer()
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      })

      // Nota: abans tenies "HHMM" (MM = mes). Ho corregeixo a "HHmm" (mm = minuts).
      const filename = `${t("app.groups")}_${format(new Date(), "ddMMyyyyHHmm")}.xlsx`
      saveAs(blob, filename)
    }

    run().catch((err) => {
      // no trenquem la UI si peta l’export
      console.error("Excel export failed:", err)
    })
  }, [data, dateFormat, t])

  // ja no renderitzem res: només disparem la descàrrega
  return null
}

const ColumnsToggle = forwardRef(({ id, children, className, 'aria-haspopup': hasPopup, 'aria-expanded': expanded, onClick }, ref) => {
  return (
    <a
      href="#/"
      role="button"
      id={id}
      className={className}
      aria-haspopup={hasPopup}
      aria-expanded={expanded}
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
    >
      {children}
    </a>
  );
})

const ColumnsMenu = forwardRef(({ children, style, className, 'aria-labelledby': labelledBy, 'x-placement': placement }, ref) => {
  const { t } = useTranslation()
  return (
    <div
      data-placement={placement}
      ref={ref}
      style={style}
      className={className}
      aria-labelledby={labelledBy}
      role="menu"
    >
      <div className="dropdown-triangle"></div>
      <fieldset>
        <legend className="visually-hidden">{t('filters.selectColumns')}</legend>
        <ul className="list--unstyled list--dropdown">
          {children}
        </ul>
      </fieldset>
    </div>
  )
})

const GroupsMenu = ({ cols = [], allCols = true }) => {
  const { t } = useTranslation()
  const gfDispatch = useGroupsFiltersDispatch()
  const { exportState, exportDispatch } = useExport()
  const { lang, env, dateFormat } = useSessionContext()

  const {
    size,
    order,
    order_by,
    total,
    sync_status,
    code,
    group_name,
    semester,
    classroom_type,
    start_date_classroom,
    end_date_classroom,
    start_date_group,
    end_date_group,
    columns,
  } = useGroupsFiltersState()

  const reset =
    (sync_status !== ist.sync_status) ||
    (code !== ist.code) ||
    (group_name !== ist.group_name) ||
    (semester !== ist.semester) ||
    (classroom_type !== ist.classroom_type) ||
    (start_date_classroom !== ist.start_date_classroom) ||
    (end_date_classroom !== ist.end_date_classroom) ||
    (start_date_group !== ist.end_date_group) ||
    (end_date_group !== ist.end_date_group)

  const onChangeLimit = (limit) => {
    gfDispatch({ type: c.SET_LIMIT, payload: limit })
  }

  const onReset = () => {
    gfDispatch({ type: c.RESET_FILTERS })
  }

  const onExport = () => {
    exportDispatch({ type: c.FETCH_REQUEST })
    getGroups({
      lang,
      env,
      order,
      order_by,
      sync_status,
      code,
      group_name,
      semester,
      classroom_type,
      start_date_group,
      end_date_group,
      start_date_classroom,
      end_date_classroom,
      size: 500,
    })
      .then((res) => {
        exportDispatch({ type: c.FETCH_SUCCESS, payload: res.content })
      })
      .catch((error) => {
        exportDispatch({ type: c.FETCH_ERROR, payload: error })
      })
  }

  const onToggleColumn = (evt, id) => {
    gfDispatch({ type: c.TOGGLE_COLUMN, payload: { id: id, show: evt.target.checked } })
  }

  const popperConfig = {
    modifiers: [
      {
        name: 'offset',
        options: { offset: [1, 8], enabled: true }
      }
    ]
  }

  const colsToggleCls = allCols ? '' : 'is-on'

  return (
    <nav
      className="nav-options nav-options--groups d-sm-flex align-items-end mb-2"
      aria-label={t("common.listOptions")}
    >
      <div className="mr-auto mb-3 mb-sm-0">
        <span className="result-total">
          <strong>{total}</strong> {t("common.results")}
        </span>
        <Limits limit={size} onChange={onChangeLimit} />
      </div>

      <ul className="list--unstyled list--inline list--options mb-3 mb-sm-0 float-right">
        {reset && (
          <li>
            <a href="#/" role="button" className="btnlink btnlink--regular" onClick={(evt) => { evt.preventDefault(); onReset() }}>
              {t("filters.clearFilters")}
              <span className="icon icon--close icon--xsmall icon--after" aria-hidden="true"></span>
            </a>
          </li>
        )}

        <li>
          <ConditionalWrapper
            condition={total > c.EXPORT_LIMIT}
            wrapper={(children) => (
              <GenericTooltip
                tooltip={t('common.exportLimitAlert', { limit: c.EXPORT_LIMIT })}
                id={`tooltip_export`}
                placement="top"
              >
                <span className="is-disabled">{children}</span>
              </GenericTooltip>
            )}
          >
            <a
              href="#/"
              role="button"
              className="btnlink btnlink--regular"
              onClick={(evt) => { evt.preventDefault(); (total <= c.EXPORT_LIMIT) && onExport() }}
              aria-disabled={total > c.EXPORT_LIMIT}
            >
              {t("actions.export")}
              <span className="icon icon--excel icon--xsmall icon--after" aria-hidden="true"></span>
            </a>
          </ConditionalWrapper>
        </li>

        <li>
          <Dropdown>
            <Dropdown.Toggle as={ColumnsToggle} id="dropdown-columns" className="btnlink btnlink--regular dropdown-toggle">
              {t("filters.showColumns")}
              <span className={`icon icon--columns icon--xsmall icon--after ${colsToggleCls}`} aria-hidden="true">
                <span className="lines"></span>
              </span>
            </Dropdown.Toggle>

            <Dropdown.Menu as={ColumnsMenu} align="right" popperConfig={popperConfig} aria-labelledby="dropdown-columns" className="dropdown-menu-columns">
              {cols.map((item, index) => (
                <li key={item.id}>
                  <div className="form-check">
                    <label htmlFor={`col-${item.id}`}>
                      <input type="checkbox" id={`col-${item.id}`} checked={columns[index].show} onChange={(evt) => onToggleColumn(evt, item.id)} />
                      <span className="icon icon--checkbox-off icon--small" aria-hidden="true"></span>
                      {item.title}
                    </label>
                  </div>
                </li>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </li>
      </ul>

      {exportState.isDownload && <ExportDownload data={exportState.content} dateFormat={dateFormat} />}
    </nav>
  )
}

export default GroupsMenu

